import {
  AfterViewInit,
  Component,
  input,
  model,
  OnChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';

import { translate } from '@jsverse/transloco';
import { Subject } from 'rxjs';

import { filter, takeUntil } from 'rxjs/operators';

import {
  ConfigurationService,
  SemantriaConfiguration,
} from 'app/providers/semantria-api/configuration.service';
import { IndustryPackService } from 'app/providers/semantria-api/industry-pack.service';
import {
  LanguageTemplate,
  LanguageTemplateService,
} from 'app/providers/semantria-api/language-template.service';
import { BriefIndustryPack } from 'app/providers/semantria-api/semantria-account.service';
import { SemantriaUsersService } from 'app/providers/semantria-api/semantria-users.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';

import { ConfigurationVersionTrackerService } from '../../services/configuration-version-tracker.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-overview',
  templateUrl: './configuration-overview.component.html',
  styleUrls: ['./configuration-overview.component.scss'],
  standalone: false,
})
export class ConfigurationOverviewComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly configuration = input<SemantriaConfiguration>();
  readonly templateConfiguration = model<any>();
  readonly industryPacks = input<BriefIndustryPack[]>([]);
  readonly languageTemplates = input<LanguageTemplate[]>([]);
  readonly isConfigLoading = input<boolean>();

  createdBy: string;
  ancestorConfiguration: any;
  configVersion: number;
  modificationDate: Date;
  modificationLabel: string;
  configLastCheckedDate: Date;
  configVersionLoading: boolean;

  unsub$ = new Subject<void>();

  constructor(
    private userService: SemantriaUsersService,
    private systemService: SystemService,
    private alertService: AlertService,
    private configService: ConfigurationService,
    private industryPackService: IndustryPackService,
    private languageTemplateService: LanguageTemplateService,
    private configVersionTracker: ConfigurationVersionTrackerService
  ) {
    this.configVersionTracker.onConfigurationChange
      .pipe(
        filter((config) => !!config),
        takeUntil(this.unsub$)
      )
      .subscribe((config) => this.setVersionData(config));
  }

  ngOnInit() {
    this.configVersionLoading = true;
    this.setTemplateConfig();
  }

  ngOnChanges(changes) {
    if (changes.configuration?.currentValue) {
      this.setVersionData(this.configuration);

      this.modificationLabel = this.configuration().removed
        ? translate('configurations.overview.deleted')
        : translate('configurations.overview.edited');
      this.createdBy = 'API';

      if (this.configuration().creator_user_id !== 'API') {
        this.userService.getUser(this.configuration().creator_user_id).then((res) => {
          this.createdBy = ((res.first_name || '') + ' ' + (res.last_name || '')).trim();
          if (!this.createdBy) {
            this.createdBy = res.email;
          }
        });
      }

      this.setTemplateConfig();
    }
  }

  private setVersionData(config) {
    this.configLastCheckedDate = new Date();
    this.configVersion = config.version ?? translate('configurations.overview.unknown');
    this.modificationDate = config.modification_date;
  }

  ngAfterViewInit() {
    if (!this.configuration().removed) {
      let getVersionCall: () => Promise<any> = this.configService.getConfiguration.bind(
        this.configService,
        this.configuration().id
      );
      if (this.industryPacks().some((ip) => ip.id === this.configuration().id)) {
        getVersionCall = this.industryPackService.getIndustryPack.bind(
          this.industryPackService,
          this.configuration().id
        );
      }
      if (this.languageTemplates().some((lt) => lt.id === this.configuration().id)) {
        getVersionCall = this.languageTemplateService.getLanguageTemplate.bind(
          this.languageTemplateService,
          this.configuration().id
        );
      }

      getVersionCall()
        .then((config) => {
          this.setVersionData(config);
        })
        .catch((error) => {
          this.alertService.showErrorToast(
            translate('configurations.overview.get-config-failed'),
            error
          );
        })
        .finally(() => (this.configVersionLoading = false));
    } else {
      this.setVersionData(this.configuration);
      this.configVersionLoading = false;
    }
  }

  ngOnDestroy() {
    this.unsub$.next();
  }

  copyConfigId() {
    this.systemService.copyToClipboard(this.configuration().id);
    this.alertService.showToast(translate('configurations.overview.config-id-copied'));
  }

  private setTemplateConfig() {
    if (this.configuration().cloned_from !== this.templateConfiguration()?.id) {
      this.templateConfiguration.set(
        this.industryPacks().find((ip) => ip.id === this.configuration().cloned_from)
      );

      if (!this.templateConfiguration) {
        this.templateConfiguration.set(
          this.languageTemplates().find((lt) => lt.id === this.configuration().cloned_from)
        );
      }
    }

    this.ancestorConfiguration = undefined;
    if (this.configuration().template_config) {
      this.ancestorConfiguration = this.industryPacks().find(
        (ip) => ip.id === this.configuration().template_config
      );

      if (!this.ancestorConfiguration) {
        this.ancestorConfiguration = this.languageTemplates().find(
          (lt) => lt.id === this.configuration().template_config
        );
      }
    }
  }
}
